/**
 * The sign-in page's decisions, kept free of React and the browser so they can be tested as rules.
 *
 * The page asks one question (the work e-mail) and the BFF decides the method (`POST /api/auth/start`). What is
 * decided HERE is only what the browser itself knows: what a typed code is, whether to show the passkey step after
 * a code, and what an invitation link fills in.
 */
import type { PasskeyAdvice } from '@portal/contracts'

export const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const CODE_LENGTH = 8

export function isEmail(value: string): boolean {
  return EMAIL_SHAPE.test(value.trim())
}

/**
 * What a person types or pastes into the code field, as the 8 digits the BFF expects: spaces, dashes and other
 * separators dropped (a code pasted as "1234 5678" or "Code: 12345678" works), never more than 8 digits.
 */
export function normalizeCode(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, CODE_LENGTH)
}

/** The step after a code, or `skip` to go straight in. */
export type PasskeyStep = 'required' | 'recommended' | 'optional' | 'skip'

export interface PasskeyStepContext {
  /** The browser can run WebAuthn at all. */
  canUsePasskeys: boolean
  /** A built-in authenticator (Windows Hello, Touch ID, a phone's screen lock) — not only a phone or key nearby. */
  platformAuthenticator: boolean
  /** "Not now" was said to the optional offer for this address on this device, recently. */
  snoozed: boolean
}

/**
 * Whether to show the passkey step after a code, and in which form:
 *  - `required` always shows (it is how the person gets in) — the step explains when the browser cannot do it;
 *  - `recommended` (administrator, or a passkey-only company) shows wherever passkeys work, phone included;
 *  - `optional` shows only where the device itself holds passkeys, and not again after "Not now" — a managed
 *    computer without Windows Hello, the reason a company allows codes, is never nagged.
 */
export function passkeyStepFor(advice: PasskeyAdvice, ctx: PasskeyStepContext): PasskeyStep {
  if (advice.need === 'required') return 'required'
  if (!ctx.canUsePasskeys) return 'skip'
  if (advice.need === 'recommended') return 'recommended'
  return ctx.platformAuthenticator && !ctx.snoozed ? 'optional' : 'skip'
}

/**
 * What a link to the sign-in page fills in: `?email=` (invitations), also from links sent before the one path
 * existed (`?mode=code|register&email=` — the mode is now the BFF's to decide, so it is ignored). Never auto-submits:
 * mail scanners open links, and a page that sent a code on load would spend the person's codes.
 */
export function prefillFromLink(params: URLSearchParams): { email: string } {
  const email = (params.get('email') ?? '').trim()
  return { email: isEmail(email) ? email : '' }
}

/** "EVT", "EVT and Acme", "EVT, Acme and Initech" — company names in a sentence. */
export function listNames(names: string[], and: string): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`
}

/** Seconds left before another code may be asked for; 0 when it may. */
export function resendWait(sentAt: number, now: number, cooldownMs: number): number {
  return Math.max(0, Math.ceil((sentAt + cooldownMs - now) / 1000))
}
